import { lazy, Suspense } from "react";
import type { ComponentType } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

import RootShell from "./RootShell";
import { withLayout } from "./layout-adapter";
import DashboardRedirect from "./DashboardRedirect";
import { RouteError } from "./stale-build";
import { usePageMeta } from "./page-meta";
import type { PageMetadata } from "./page-meta";

// Layouts ported from app/<route>/layout.{tsx,jsx}. Each one takes
// `{ children }` and renders sidebars / topbars / wrappers; withLayout
// re-renders them with React Router's <Outlet /> as their children.
import AgencyLayout from "../app/dashboard/agency/layout";
import AdminLayout from "../app/dashboard/admin/layout";
import RecruitersLayout from "../app/recruiters/layout";
import FeaturesLayout from "../app/features/layout";
import { CANDIDATE_META } from "../lib/native/candidateLangs.js";
import { CANDIDATE_SLUGS, nativeCandidatePath } from "../lib/native/candidatePages.js";

const AgencyLayoutRoute = withLayout(AgencyLayout);
const AdminLayoutRoute = withLayout(AdminLayout);
const RecruitersLayoutRoute = withLayout(RecruitersLayout);
const FeaturesLayoutRoute = withLayout(FeaturesLayout);

// Mirrors the existing app/ filesystem routing. Each leaf imports the page
// component directly from the existing app/ folder so we don't have to move
// 80 files in this first pass.

const lazyPage = (
  loader: () => Promise<{
    default: ComponentType<unknown>;
    metadata?: PageMetadata;
    useMetadata?: () => PageMetadata;
  }>
) => {
  // Pages still carry their Next.js-era `export const metadata`; apply it to
  // the document head when the route mounts (src/page-meta.tsx). Dynamic
  // routes (blog/:slug) export a `useMetadata` hook instead — without it the
  // wrapper's usePageMeta(undefined) ran after the page's own effect and reset
  // every post's title to the site default in the rendered DOM.
  const C = lazy(async () => {
    const mod = await loader();
    const Page = mod.default;
    const useMeta = mod.useMetadata ?? (() => mod.metadata);
    const WithMeta = (props: Record<string, unknown>) => {
      usePageMeta(useMeta());
      return <Page {...props} />;
    };
    return { default: WithMeta };
  });
  return (
    // The fallback must reserve space: with fallback={null} the footer paints
    // at the top of the viewport and gets pushed down a full screen when the
    // page chunk lands — a 0.51 CLS on every cold load (GSC Core Web Vitals,
    // 2026-06-08).
    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
      <C />
    </Suspense>
  );
};

// Start downloading the landing route's page chunk immediately — in parallel
// with the main bundle's execution — instead of after the router has booted
// and matched. The serial main-bundle → route-chunk waterfall added a full
// network hop to LCP on direct loads of the marketing pages (GSC Core Web
// Vitals, 2026-06-08; Lighthouse showed LCP 7.4s on the homepage hero).
// Vite resolves these import() calls to the same chunks as the route table's
// loaders, so a prefetched module is simply already in flight when the route
// mounts. Covers the public marketing routes only; app routes load as before.
const PREFETCH_ON_BOOT: Record<string, () => Promise<unknown>> = {
  "/": () => import("../app/page"),
  "/about": () => import("../app/about/page"),
  "/contact": () => import("../app/contact/page"),
  "/book-a-demo": () => import("../app/book-a-demo/page"),
  "/blog": () => import("../app/blog/page"),
  "/tools": () => import("../app/tools/page"),
  "/recruiters": () => import("../app/recruiters/page"),
  "/recruiters/ai-interview-bot": () => import("../app/recruiters/ai-interview-bot/page"),
  "/recruiters/ai-interview-software": () => import("../app/recruiters/ai-interview-software/page"),
  "/recruiters/ai-interviewer": () => import("../app/recruiters/ai-interviewer/page"),
  "/recruiters/ai-phone-screening": () => import("../app/recruiters/ai-phone-screening/page"),
  "/recruiters/ai-video-interview-software": () => import("../app/recruiters/ai-video-interview-software/page"),
  "/recruiters/interview-highlight-clips": () => import("../app/recruiters/interview-highlight-clips/page"),
  "/recruiters/candidate-location-check": () => import("../app/recruiters/candidate-location-check/page"),
  "/recruiters/call-to-submittal": () => import("../app/recruiters/call-to-submittal/page"),
  "/recruiters/joining-readiness": () => import("../app/recruiters/joining-readiness/page"),
  "/recruiters/ai-resume-screening": () => import("../app/recruiters/ai-resume-screening/page"),
  "/recruiters/agentic-resume-search": () => import("../app/recruiters/agentic-resume-search/page"),
  "/recruiters/career-page-builder": () => import("../app/recruiters/career-page-builder/page"),
  "/candidates": () => import("../app/candidates/page"),
  "/features/ai-mock-interview": () => import("../app/features/ai-mock-interview/page"),
  "/features/ai-video-interview": () => import("../app/features/ai-video-interview/page"),
  "/features/interview-warmup": () => import("../app/features/interview-warmup/page"),
};
PREFETCH_ON_BOOT[window.location.pathname.replace(/\/+$/, "") || "/"]?.();

// /<code>/features/<slug> for every language that has a copy file (lib/native/candidate/).
const nativeCandidateRoutes = CANDIDATE_META.flatMap((m) =>
  CANDIDATE_SLUGS.map((slug) => ({
    path: nativeCandidatePath(m.code, slug).slice(1),
    element: lazyPage(() => import("../app/native/candidate/page")),
  })),
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: lazyPage(() => import("../app/page")) },
      { path: "about", element: lazyPage(() => import("../app/about/page")) },
      { path: "candidates", element: lazyPage(() => import("../app/candidates/page")) },
      { path: "contact", element: lazyPage(() => import("../app/contact/page")) },
      { path: "newsletter", element: lazyPage(() => import("../app/newsletter/page")) },
      { path: "book-a-demo", element: lazyPage(() => import("../app/book-a-demo/page")) },
      { path: "pricing", element: lazyPage(() => import("../app/pricing/page")) },
      { path: "privacy-policy", element: lazyPage(() => import("../app/privacy-policy/page")) },
      { path: "terms-of-use", element: lazyPage(() => import("../app/terms-of-use/page")) },
      { path: "refund-policy", element: lazyPage(() => import("../app/refund-policy/page")) },
      { path: "report-preview", element: lazyPage(() => import("../app/report-preview/page")) },
      { path: "shared-video", element: lazyPage(() => import("../app/shared-video/page")) },
      { path: "shared-clip", element: lazyPage(() => import("../app/shared-clip/page")) },
      { path: "connect", element: lazyPage(() => import("../app/connect/page")) },
      { path: "demo/post-interview-feedback", element: lazyPage(() => import("../app/demo/post-interview-feedback/page")) },

      // Content hubs (lib/hubs): one index template and one article template.
      { path: "interview-questions", element: lazyPage(() => import("../app/hub/index-page")) },
      { path: "interview-questions/:slug", element: lazyPage(() => import("../app/hub/article-page")) },
      // Printable screening kit linked from the kit email (noindex, client rendered).
      { path: "kits/:slug", element: lazyPage(() => import("../app/kits/[slug]/page")) },
      { path: "compare", element: lazyPage(() => import("../app/hub/index-page")) },
      { path: "compare/:slug", element: lazyPage(() => import("../app/hub/article-page")) },
      { path: "industries", element: lazyPage(() => import("../app/hub/index-page")) },
      { path: "industries/:slug", element: lazyPage(() => import("../app/hub/article-page")) },
      { path: "integrations", element: lazyPage(() => import("../app/hub/index-page")) },
      { path: "integrations/:slug", element: lazyPage(() => import("../app/hub/article-page")) },
      { path: "security", element: lazyPage(() => import("../app/hub/article-page")) },
      { path: "languages", element: lazyPage(() => import("../app/hub/article-page")) },
      // Native-language recruiter pages, one template (lib/native/aiiaLanding.js).
      { path: "de", element: lazyPage(() => import("../app/native/page")) },
      { path: "fr", element: lazyPage(() => import("../app/native/page")) },
      { path: "es", element: lazyPage(() => import("../app/native/page")) },
      { path: "sv", element: lazyPage(() => import("../app/native/page")) },
      { path: "it", element: lazyPage(() => import("../app/native/page")) },
      // Native-language candidate pages, one template (lib/native/candidate/<code>.js).
      ...nativeCandidateRoutes,

      { path: "blog", element: lazyPage(() => import("../app/blog/page")) },
      { path: "blog/:slug", element: lazyPage(() => import("../app/blog/[slug]/page")) },

      { path: "tools", element: lazyPage(() => import("../app/tools/page")) },
      {
        path: "tools/resume-interview-questions",
        element: lazyPage(() => import("../app/tools/resume-interview-questions/page")),
      },
      {
        path: "tools/interview-kit-generator",
        element: lazyPage(() => import("../app/tools/interview-kit-generator/page")),
      },
      {
        path: "tools/boolean-search-generator",
        element: lazyPage(() => import("../app/tools/boolean-search-generator/page")),
      },
      {
        path: "tools/job-description-generator",
        element: lazyPage(() => import("../app/tools/job-description-generator/page")),
      },
      {
        path: "tools/cost-per-hire-calculator",
        element: lazyPage(() => import("../app/tools/cost-per-hire-calculator/page")),
      },

      {
        path: "tools/reference-check-questions",
        element: lazyPage(() => import("../app/tools/reference-check-questions/page")),
      },
      {
        path: "tools/ai-resume-checker",
        element: lazyPage(() => import("../app/tools/ai-resume-checker/page")),
      },

      // Sign-in is for recruiter accounts only. Candidates have no account:
      // they open /my-interviews with an emailed code. The old role chooser
      // and the old sign-up address stay as redirects for stale links.
      { path: "auth", element: <Navigate to="/auth/login" replace /> },
      { path: "auth/login", element: lazyPage(() => import("../app/auth/login/page")) },
      { path: "auth/register", element: lazyPage(() => import("../app/auth/register/page")) },

      { path: "certificate/:id", element: lazyPage(() => import("../app/certificate/[id]/page")) },
      { path: "interview/:meetingId", element: lazyPage(() => import("../app/interview/[meetingId]/page")) },
      // Short-link resolver for WhatsApp/SMS (`/i/<id>`) — forwards to the full
      // `/interview/<id>?...` URL the room needs. See app/i/[meetingId]/page.
      { path: "i/:meetingId", element: lazyPage(() => import("../app/i/[meetingId]/page")) },
      { path: "my-interviews", element: lazyPage(() => import("../app/my-interviews/page")) },
      { path: "careers/:slug", element: lazyPage(() => import("../app/careers/[slug]/page")) },
      { path: "careers/:slug/:roleSlug", element: lazyPage(() => import("../app/careers/[slug]/[roleSlug]/page")) },

      // Bare /dashboard role-redirect — replaces what Next.js's middleware
      // could have done. Several call sites (PaymentPopup, pricingPlans)
      // push to /dashboard expecting "land me on the right home for my role".
      { path: "dashboard", element: <DashboardRedirect /> },

      // recruiters
      {
        path: "recruiters",
        element: <RecruitersLayoutRoute />,
        children: [
          { index: true, element: lazyPage(() => import("../app/recruiters/page")) },
          { path: "ai-interview-bot", element: lazyPage(() => import("../app/recruiters/ai-interview-bot/page")) },
          { path: "ai-interview-software", element: lazyPage(() => import("../app/recruiters/ai-interview-software/page")) },
          { path: "ai-interviewer", element: lazyPage(() => import("../app/recruiters/ai-interviewer/page")) },
          { path: "ai-phone-screening", element: lazyPage(() => import("../app/recruiters/ai-phone-screening/page")) },
          { path: "ai-video-interview-software", element: lazyPage(() => import("../app/recruiters/ai-video-interview-software/page")) },
          { path: "interview-highlight-clips", element: lazyPage(() => import("../app/recruiters/interview-highlight-clips/page")) },
          { path: "candidate-location-check", element: lazyPage(() => import("../app/recruiters/candidate-location-check/page")) },
          { path: "call-to-submittal", element: lazyPage(() => import("../app/recruiters/call-to-submittal/page")) },
          { path: "joining-readiness", element: lazyPage(() => import("../app/recruiters/joining-readiness/page")) },
          // 301'd in nginx; this covers stale client-side links.
          { path: "ai-mock-interviewer", element: <Navigate to="/features/ai-mock-interview" replace /> },
          { path: "ai-resume-screening", element: lazyPage(() => import("../app/recruiters/ai-resume-screening/page")) },
          { path: "agentic-resume-search", element: lazyPage(() => import("../app/recruiters/agentic-resume-search/page")) },
          { path: "career-page-builder", element: lazyPage(() => import("../app/recruiters/career-page-builder/page")) },
        ],
      },

      // features
      {
        path: "features",
        element: <FeaturesLayoutRoute />,
        children: [
          // 301'd in nginx; this covers stale client-side links.
          { path: "ai-interviewer", element: <Navigate to="/recruiters/ai-interviewer" replace /> },
          { path: "ai-mock-interview", element: lazyPage(() => import("../app/features/ai-mock-interview/page")) },
          { path: "ai-video-interview", element: lazyPage(() => import("../app/features/ai-video-interview/page")) },
          { path: "interview-warmup", element: lazyPage(() => import("../app/features/interview-warmup/page")) },
        ],
      },

      // dashboard / agency (HR)
      {
        path: "dashboard/agency",
        element: <AgencyLayoutRoute />,
        children: [
          { index: true, element: lazyPage(() => import("../app/dashboard/agency/page")) },
          { path: "agents", element: lazyPage(() => import("../app/dashboard/agency/agents/page")) },
          { path: "assets", element: lazyPage(() => import("../app/dashboard/agency/assets/page")) },
          { path: "assets/:roleId", element: lazyPage(() => import("../app/dashboard/agency/assets/[roleId]/page")) },
          { path: "candidates", element: lazyPage(() => import("../app/dashboard/agency/candidates/page")) },
          { path: "calls", element: lazyPage(() => import("../app/dashboard/agency/calls/page")) },
          { path: "calls/:callId", element: lazyPage(() => import("../app/dashboard/agency/calls/[callId]/page")) },
          { path: "create-interview", element: lazyPage(() => import("../app/dashboard/agency/create-interview/page")) },
          { path: "data", element: lazyPage(() => import("../app/dashboard/agency/data/page")) },
          { path: "data/:folderId", element: lazyPage(() => import("../app/dashboard/agency/data/[folderId]/page")) },
          { path: "feedback/:id", element: lazyPage(() => import("../app/dashboard/agency/feedback/[id]/page")) },
          { path: "interview-room", element: lazyPage(() => import("../app/dashboard/agency/interview-room/page")) },
          { path: "interviews", element: lazyPage(() => import("../app/dashboard/agency/interviews/page")) },
          { path: "interviews/:roleId", element: lazyPage(() => import("../app/dashboard/agency/interviews/[roleId]/page")) },
          { path: "interviews/pipeline/:roleId", element: lazyPage(() => import("../app/dashboard/agency/interviews/pipeline/[roleId]/page")) },
          { path: "meeting-login", element: lazyPage(() => import("../app/dashboard/agency/meeting-login/page")) },
          { path: "modify-interview", element: lazyPage(() => import("../app/dashboard/agency/modify-interview/page")) },
          { path: "profile", element: lazyPage(() => import("../app/dashboard/agency/profile/page")) },
          { path: "report/:id", element: lazyPage(() => import("../app/dashboard/agency/report/[id]/page")) },
          { path: "reports", element: lazyPage(() => import("../app/dashboard/agency/reports/page")) },
          { path: "reports/:roleId", element: lazyPage(() => import("../app/dashboard/agency/reports/[roleId]/page")) },
          { path: "reviews", element: lazyPage(() => import("../app/dashboard/agency/reviews/page")) },
          { path: "reviews/:roleId", element: lazyPage(() => import("../app/dashboard/agency/reviews/[roleId]/page")) },
          { path: "screening", element: lazyPage(() => import("../app/dashboard/agency/screening/page")) },
          { path: "screening/new", element: lazyPage(() => import("../app/dashboard/agency/screening/new/page")) },
          { path: "screening/:roleId", element: lazyPage(() => import("../app/dashboard/agency/screening/[roleId]/page")) },
          { path: "roles", element: lazyPage(() => import("../app/dashboard/agency/roles/page")) },
          { path: "roles/new", element: lazyPage(() => import("../app/dashboard/agency/roles/new/page")) },
          { path: "roles/:roleId/edit", element: lazyPage(() => import("../app/dashboard/agency/roles/[roleId]/edit/page")) },
          { path: "roles/:roleId/interview-setup", element: lazyPage(() => import("../app/dashboard/agency/roles/[roleId]/interview-setup/page")) },
          { path: "roles/:roleId", element: lazyPage(() => import("../app/dashboard/agency/roles/[roleId]/page")) },
          { path: "company", element: lazyPage(() => import("../app/dashboard/agency/company/page")) },
          { path: "company/career-page", element: lazyPage(() => import("../app/dashboard/agency/company/career-page/page")) },
          { path: "company/emails", element: lazyPage(() => import("../app/dashboard/agency/company/emails/page")) },
          { path: "company/integrations", element: lazyPage(() => import("../app/dashboard/agency/company/integrations/page")) },
          { path: "branding", element: lazyPage(() => import("../app/dashboard/agency/branding/page")) },
          { path: "branding/career-page", element: lazyPage(() => import("../app/dashboard/agency/branding/career-page/page")) },
          { path: "branding/emails", element: lazyPage(() => import("../app/dashboard/agency/branding/emails/page")) },
          { path: "branding/integrations", element: lazyPage(() => import("../app/dashboard/agency/branding/integrations/page")) },
          { path: "careers-settings", element: lazyPage(() => import("../app/dashboard/agency/careers-settings/page")) },
          { path: "talent-pool", element: lazyPage(() => import("../app/dashboard/agency/talent-pool/page")) },
          { path: "integrations", element: lazyPage(() => import("../app/dashboard/agency/integrations/page")) },
        ],
      },

      // dashboard / admin
      {
        path: "dashboard/admin",
        element: <AdminLayoutRoute />,
        children: [
          { index: true, element: lazyPage(() => import("../app/dashboard/admin/page")) },
          { path: "activity-tracking", element: lazyPage(() => import("../app/dashboard/admin/activity-tracking/page")) },
          { path: "analytics", element: lazyPage(() => import("../app/dashboard/admin/analytics/page")) },
          { path: "hr-management", element: lazyPage(() => import("../app/dashboard/admin/hr-management/page")) },
          { path: "interview-results", element: lazyPage(() => import("../app/dashboard/admin/interview-results/page")) },
        ],
      },

      // The candidate dashboard is retired (Deepak, 2026-10-13). Its addresses
      // are in old emails and bookmarks, so they open the viewing page.
      { path: "dashboard/candidate/*", element: <Navigate to="/my-interviews" replace /> },

      // Any address no route above matches. Without this the router raised a
      // 404 into errorElement and the visitor saw the stale-build screen. The
      // id is how app/ProtectedRoute.jsx knows to use the marketing shell.
      { id: "not-found", path: "*", element: lazyPage(() => import("../app/not-found/page")) },
    ],
  },
]);
